import { useState, useEffect, useCallback } from 'react';

export const useTheme = () => {
  const [currentTheme, setCurrentTheme] = useState('light');

  const updateDocumentTheme = useCallback((theme) => {
    // Update data attribute for CSS targeting
    document.documentElement?.setAttribute('data-theme', theme);
    
    // Update body class for theme-specific styling
    document.body?.classList?.remove('light', 'dark');
    document.body?.classList?.add(theme);
    
    // Update CSS custom properties dynamically
    if (theme === 'dark') {
      document.documentElement?.style?.setProperty('--color-background', '#0F172A');
      document.documentElement?.style?.setProperty('--color-foreground', '#F8FAFC');
      document.documentElement?.style?.setProperty('--color-card', '#1E293B');
      document.documentElement?.style?.setProperty('--color-card-foreground', '#F8FAFC');
      document.documentElement?.style?.setProperty('--color-popover', '#1E293B');
      document.documentElement?.style?.setProperty('--color-popover-foreground', '#F8FAFC');
      document.documentElement?.style?.setProperty('--color-muted', '#334155');
      document.documentElement?.style?.setProperty('--color-muted-foreground', '#94A3B8');
      document.documentElement?.style?.setProperty('--color-border', '#475569');
      document.documentElement?.style?.setProperty('--color-input', '#1E293B');
      document.documentElement?.style?.setProperty('--color-surface', '#1E293B');
      document.documentElement?.style?.setProperty('--color-text-primary', '#F8FAFC');
      document.documentElement?.style?.setProperty('--color-text-secondary', '#94A3B8');
    } else {
      // Light mode (default values)
      document.documentElement?.style?.setProperty('--color-background', '#F8FAFC');
      document.documentElement?.style?.setProperty('--color-foreground', '#0F172A');
      document.documentElement?.style?.setProperty('--color-card', '#FFFFFF');
      document.documentElement?.style?.setProperty('--color-card-foreground', '#0F172A');
      document.documentElement?.style?.setProperty('--color-popover', '#FFFFFF');
      document.documentElement?.style?.setProperty('--color-popover-foreground', '#0F172A');
      document.documentElement?.style?.setProperty('--color-muted', '#F1F5F9');
      document.documentElement?.style?.setProperty('--color-muted-foreground', '#64748B');
      document.documentElement?.style?.setProperty('--color-border', '#E2E8F0');
      document.documentElement?.style?.setProperty('--color-input', '#FFFFFF');
      document.documentElement?.style?.setProperty('--color-surface', '#FFFFFF');
      document.documentElement?.style?.setProperty('--color-text-primary', '#0F172A');
      document.documentElement?.style?.setProperty('--color-text-secondary', '#64748B');
    }
  }, []);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPreference = window.matchMedia('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
    const initialTheme = savedTheme || systemPreference;
    
    setCurrentTheme(initialTheme);
    updateDocumentTheme(initialTheme);
  }, [updateDocumentTheme]);

  const changeTheme = useCallback((newTheme) => {
    if (newTheme !== currentTheme && (newTheme === 'light' || newTheme === 'dark')) {
      setCurrentTheme(newTheme);
      localStorage.setItem('theme', newTheme);
      updateDocumentTheme(newTheme);
      
      // Dispatch custom event for components that need to know about theme changes
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: newTheme }));
    }
  }, [currentTheme, updateDocumentTheme]);

  const toggleTheme = useCallback(() => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    changeTheme(newTheme);
  }, [currentTheme, changeTheme]);

  const isDark = currentTheme === 'dark';
  const isLight = currentTheme === 'light';

  return {
    currentTheme,
    changeTheme,
    toggleTheme,
    isDark,
    isLight,
  };
};